/**
 * Shared enums / constant unions used across client, server, and agent.
 * Kept as `const` objects (erasable, no runtime enum) so they are safe to import
 * from both the bundled client and the tsx-run server.
 */

/** Facebook entity status values used by the app for start/stop. */
export const EntityStatus = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  ARCHIVED: 'ARCHIVED',
  DELETED: 'DELETED',
  DISABLED: 'DISABLED',
} as const;
export type EntityStatus = (typeof EntityStatus)[keyof typeof EntityStatus];

/** Rule trigger types. */
export const TriggerType = {
  REVENUE_THRESHOLD: 'REVENUE_THRESHOLD',
  CPA_THRESHOLD: 'CPA_THRESHOLD',
  ROAS_THRESHOLD: 'ROAS_THRESHOLD',
  SPEND_THRESHOLD: 'SPEND_THRESHOLD',
} as const;
export type TriggerType = (typeof TriggerType)[keyof typeof TriggerType];

/** Rule action types (what the rule does when triggered). */
export const ActionType = {
  INCREASE_BUDGET: 'INCREASE_BUDGET',
  DECREASE_BUDGET: 'DECREASE_BUDGET',
  PAUSE: 'PAUSE',
  ACTIVATE: 'ACTIVATE',
} as const;
export type ActionType = (typeof ActionType)[keyof typeof ActionType];

/** Target of a rule / agent action. */
export const TargetType = {
  CAMPAIGN: 'CAMPAIGN',
  AD_SET: 'AD_SET',
} as const;
export type TargetType = (typeof TargetType)[keyof typeof TargetType];

/**
 * Action types the LLM may emit in its decision JSON.
 * See LLM Prompt Engineering Guide §3.
 */
export const AgentActionType = {
  INCREASE_BUDGET: 'INCREASE_BUDGET',
  DECREASE_BUDGET: 'DECREASE_BUDGET',
  PAUSE_CAMPAIGN: 'PAUSE_CAMPAIGN',
  ACTIVATE_CAMPAIGN: 'ACTIVATE_CAMPAIGN',
  PAUSE_ADSET: 'PAUSE_ADSET',
  ACTIVATE_ADSET: 'ACTIVATE_ADSET',
  NO_ACTION: 'NO_ACTION',
} as const;
export type AgentActionType = (typeof AgentActionType)[keyof typeof AgentActionType];

/** Agent log execution status. */
export const LogStatus = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  GUARDRAIL_BLOCKED: 'GUARDRAIL_BLOCKED',
} as const;
export type LogStatus = (typeof LogStatus)[keyof typeof LogStatus];

/** Agent log event types. */
export const AgentEventType = {
  BUDGET_INCREASED: 'BUDGET_INCREASED',
  BUDGET_DECREASED: 'BUDGET_DECREASED',
  CAMPAIGN_PAUSED: 'CAMPAIGN_PAUSED',
  CAMPAIGN_ACTIVATED: 'CAMPAIGN_ACTIVATED',
  ADSET_PAUSED: 'ADSET_PAUSED',
  ADSET_ACTIVATED: 'ADSET_ACTIVATED',
  NO_ACTION: 'NO_ACTION',
  GUARDRAIL_TRIGGERED: 'GUARDRAIL_TRIGGERED',
  DAY_HALTED: 'DAY_HALTED',
  ERROR: 'ERROR',
} as const;
export type AgentEventType = (typeof AgentEventType)[keyof typeof AgentEventType];

/** Standardized API error codes. */
export const ErrorCode = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  FACEBOOK_API_ERROR: 'FACEBOOK_API_ERROR',
  LLM_ERROR: 'LLM_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];
